import type { Dispatch, SetStateAction } from "react";

import {
  createWebhookParseState,
  processWebhookChunk,
} from "./webhookStreamParser";
import type { ReasoningInterval } from "./openCodeParser";
import type { AIResponse } from "./types";

export async function readAssistantStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  assistantId: string,
  setMessages: Dispatch<SetStateAction<AIResponse[]>>,
  setIsLoading: Dispatch<SetStateAction<boolean>>,
): Promise<void> {
  const decoder = new TextDecoder();
  const wrapperState = createWebhookParseState();
  let streamEndedByEvent = false;

  // Measure thinking time as wall-clock elapsed from the first reasoning chunk
  // arriving at the client to the last, so it reflects the actual wait time the
  // user experiences rather than the narrower server-side timestamps.
  let thinkingStartedAt: number | undefined;
  let thinkingEndedAt: number | undefined;
  let thinkingSeconds: number | undefined;
  let loadingCleared = false;

  const computeThinkingSeconds = (): number | undefined => {
    if (thinkingStartedAt === undefined || thinkingEndedAt === undefined) {
      return undefined;
    }
    const totalMs = thinkingEndedAt - thinkingStartedAt;
    return totalMs > 0 ? Math.round(totalMs / 1000) : undefined;
  };

  const clearLoadingOnce = () => {
    if (!loadingCleared) {
      loadingCleared = true;
      setIsLoading(false);
    }
  };

  const onThinking = (text: string, _intervals?: ReasoningInterval[]) => {
    clearLoadingOnce();
    const now = Date.now();
    if (thinkingStartedAt === undefined) thinkingStartedAt = now;
    thinkingEndedAt = now;
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === assistantId
          ? { ...msg, thinking: (msg.thinking ?? "") + text }
          : msg,
      ),
    );
  };

  const onContent = (markdown: string) => {
    clearLoadingOnce();
    thinkingSeconds = computeThinkingSeconds() ?? thinkingSeconds;
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === assistantId
          ? { ...msg, content: msg.content + markdown, thinkingSeconds }
          : msg,
      ),
    );
  };

  const onEnd = () => {
    streamEndedByEvent = true;
    clearLoadingOnce();
    thinkingSeconds = computeThinkingSeconds() ?? thinkingSeconds;
    setMessages((prev) => {
      const msg = prev.find((m) => m.id === assistantId);
      if (msg && !msg.content && !msg.thinking) {
        return prev.filter((m) => m.id !== assistantId);
      }
      return prev.map((m) =>
        m.id === assistantId
          ? { ...m, isStreaming: false, thinkingSeconds }
          : m,
      );
    });
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      if (!streamEndedByEvent) onEnd();
      break;
    }
    processWebhookChunk(decoder.decode(value, { stream: true }), wrapperState, {
      onContent,
      onThinking,
      onEnd,
    });
  }
}
